import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Separator } from './Separator'

interface SpeciesIllustration {
  id: number
  latin_name: string
  common_name_pl: string
  illustration_url: string
  thumbnail_url: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSelectIllustration: (speciesId: number) => void
  onUploadPhoto: (file: File) => void
  currentSpeciesName?: string | null
}

export function PhotoPickerModal({ isOpen, onClose, onSelectIllustration, onUploadPhoto, currentSpeciesName }: Props) {
  const [illustrations, setIllustrations] = useState<SpeciesIllustration[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    fetch('/plants/species_illustrations')
      .then(res => res.json())
      .then(data => {
        setIllustrations(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [isOpen])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onUploadPhoto(file)
      onClose()
    }
  }, [onUploadPhoto, onClose])

  const handleSelect = useCallback((speciesId: number) => {
    onSelectIllustration(speciesId)
    onClose()
  }, [onSelectIllustration, onClose])

  if (!isOpen) return null

  const sorted = [...illustrations]
    .filter(i => !filter || i.latin_name.toLowerCase().includes(filter.toLowerCase()) || i.common_name_pl.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      if (currentSpeciesName) {
        const aMatch = a.latin_name === currentSpeciesName ? -1 : 0
        const bMatch = b.latin_name === currentSpeciesName ? -1 : 0
        if (aMatch !== bMatch) return aMatch - bMatch
      }
      return a.common_name_pl.localeCompare(b.common_name_pl)
    })

  const modal = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-md"
      style={{ background: 'rgba(58,42,24,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      <div
        className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded animate-[modal-in_0.2s_ease-out]"
        style={{ background: 'var(--paper)', border: '2px double var(--frame)', boxShadow: '0 8px 32px rgba(58,42,24,0.3)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-lg pt-lg pb-sm shrink-0">
          <h3 className="font-display text-lg uppercase tracking-wider text-ink">Wybierz zdjęcie</h3>
          <button onClick={onClose} className="text-frame text-xl hover:text-ink cursor-pointer bg-transparent border-none">&times;</button>
        </div>

        {/* Upload own photo */}
        <div className="px-lg pb-sm shrink-0">
          <label
            className="flex items-center justify-center gap-sm py-md cursor-pointer transition-colors hover:bg-botanical-light rounded"
            style={{ border: '1px dashed var(--botanical)', background: 'var(--paper-dark)' }}
          >
            <span className="text-xl">📷</span>
            <span className="text-botanical text-sm font-medium">Wgraj własne zdjęcie</span>
            <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          </label>
        </div>

        <div className="px-lg shrink-0">
          <Separator />
          <p className="font-label text-[0.65rem] uppercase tracking-[0.12em] text-frame mb-sm">Lub wybierz ilustrację gatunku</p>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Szukaj gatunku..."
            className="form-input mb-sm"
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-lg pb-lg" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--frame) var(--paper-dark)' }}>
          {loading ? (
            <p className="text-center text-frame text-sm italic py-lg">Ładowanie ilustracji...</p>
          ) : sorted.length === 0 ? (
            <p className="text-center text-frame text-sm italic py-lg">Brak pasujących ilustracji</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
              {sorted.map(ill => (
                <button
                  key={ill.id}
                  onClick={() => handleSelect(ill.id)}
                  className={`flex flex-col items-center p-xs cursor-pointer bg-transparent border-2 rounded transition-all hover:border-botanical hover:shadow-hover ${
                    ill.latin_name === currentSpeciesName ? 'border-botanical bg-botanical-light' : 'border-transparent'
                  }`}
                  style={{ minWidth: 0 }}
                >
                  <div className="w-full aspect-square overflow-hidden rounded-sm" style={{ border: '1px solid var(--desk)', background: 'var(--paper-dark)' }}>
                    <img
                      src={ill.thumbnail_url}
                      alt={ill.common_name_pl}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-[0.75rem] text-ink mt-xs text-center leading-tight line-clamp-1">
                    {ill.common_name_pl}
                  </span>
                  <span className="text-[0.6rem] text-frame italic text-center leading-tight line-clamp-1">
                    {ill.latin_name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
